/**
 * SyncHealthWidget
 * ----------------
 * Reusable widget showing per-villa Airbnb iCal sync status.
 * Mounted on:
 *  - /admin           (JWT auth)  → fetches from /api/ical/health
 *  - /admin-cal/:tok  (token auth) → fetches from /api/admin-cal/:tok/ical/health
 *
 * Props:
 *  - fetchHealth()          returns axios promise resolving to health payload
 *  - triggerSync(villaId)   returns axios promise (editor only). Omit to hide sync buttons.
 *  - copyUrlBaseOverride    e.g. "https://royalgardenvillas.in" — used to build absolute iCal export URLs for copying
 *  - compact                if true, renders a denser variant for the mobile PWA
 */
import React, { useCallback, useEffect, useState } from 'react';
import { FiRefreshCw, FiCopy, FiAlertTriangle, FiCheckCircle, FiClock, FiChevronDown, FiChevronUp } from 'react-icons/fi';

const fmtAgo = (secs) => {
  if (secs == null) return 'Never';
  if (secs < 60) return `${secs}s ago`;
  const m = Math.floor(secs / 60);
  if (m < 60) return `${m} min${m !== 1 ? 's' : ''} ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h !== 1 ? 's' : ''} ago`;
  return `${Math.floor(h / 24)} day${Math.floor(h / 24) !== 1 ? 's' : ''} ago`;
};

const SyncHealthWidget = ({ fetchHealth, triggerSync, copyUrlBaseOverride, compact = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(null); // villa_id currently syncing
  const [copied, setCopied] = useState(null); // villa_id of last copied URL
  const [expanded, setExpanded] = useState(!compact);

  const load = useCallback(async () => {
    try {
      const res = await fetchHealth();
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load sync health');
    } finally {
      setLoading(false);
    }
  }, [fetchHealth]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30_000); // auto-refresh every 30s
    return () => clearInterval(interval);
  }, [load]);

  const handleSync = async (villaId) => {
    if (!triggerSync) return;
    setSyncing(villaId);
    try {
      await triggerSync(villaId);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(null);
    }
  };

  const handleCopy = (villaId, url) => {
    const base = copyUrlBaseOverride || window.location.origin;
    const absoluteUrl = url.startsWith('http') ? url : `${base}${url}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(absoluteUrl);
    } else {
      const t = document.createElement('textarea');
      t.value = absoluteUrl; document.body.appendChild(t); t.select();
      try { document.execCommand('copy'); } catch (_) {}
      document.body.removeChild(t);
    }
    setCopied(villaId);
    setTimeout(() => setCopied(null), 2000);
  };

  if (loading && !data) {
    return (
      <div className="bg-white rounded-xl p-4 border border-gray-200" data-testid="sync-health-loading">
        <div className="animate-pulse text-xs text-gray-500">Loading sync status...</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-xs text-red-700" data-testid="sync-health-error">
        {error}
      </div>
    );
  }
  if (!data) return null;

  const outboundMap = Object.fromEntries((data.outbound_ical_urls || []).map((u) => [u.villa_id, u.url]));

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm" data-testid="sync-health-widget">
      {/* Header */}
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center justify-between px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors"
        data-testid="sync-health-toggle"
      >
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${data.any_stale ? 'bg-red-500 animate-pulse' : 'bg-green-500'}`} />
          <div className="text-left">
            <p className="text-sm font-bold text-[#1A1A2E]">Airbnb Sync Health</p>
            <p className="text-[10px] text-gray-500">
              {data.any_stale ? 'One or more villas are out of sync' : 'All 4 villas syncing normally'}
              {' · Auto-sync every ~'}{Math.round((data.auto_sync_interval_seconds || 3600) / 60)}min
            </p>
          </div>
        </div>
        {expanded ? <FiChevronUp className="w-4 h-4 text-gray-400" /> : <FiChevronDown className="w-4 h-4 text-gray-400" />}
      </button>

      {expanded && (
        <div className="p-3 space-y-2">
          {/* Warning banner */}
          {data.any_stale && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-[11px] text-red-700" data-testid="sync-stale-banner">
              <FiAlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                <strong>Sync warning:</strong> at least one villa hasn&apos;t polled Airbnb in over {Math.round(data.stale_threshold_seconds / 60)} minutes. Tap the villa&apos;s <em>Sync Now</em> button below to force refresh.
              </span>
            </div>
          )}

          {/* Villa rows */}
          {data.villas.map((v) => {
            const isSyncing = syncing === v.villa_id;
            const errored = v.last_sync_status === 'error';
            const stale = v.is_stale;
            const outboundUrl = outboundMap[v.villa_id];
            return (
              <div
                key={v.villa_id}
                data-testid={`sync-row-${v.villa_id}`}
                className={`rounded-lg border p-3 ${errored ? 'bg-red-50 border-red-200' : stale ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-100'}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      {errored ? (
                        <FiAlertTriangle className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />
                      ) : stale ? (
                        <FiClock className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                      ) : (
                        <FiCheckCircle className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                      )}
                      <p className="text-xs font-bold text-[#1A1A2E] truncate">{v.bhk}BHK · {v.villa_name}</p>
                    </div>
                    <div className="mt-1 flex items-center flex-wrap gap-x-3 gap-y-0.5">
                      <span className="text-[10px] text-gray-600">
                        Last synced <strong>{fmtAgo(v.seconds_since_sync)}</strong>
                      </span>
                      <span className="text-[10px] text-gray-600">
                        {v.airbnb_blocks_count} Airbnb block{v.airbnb_blocks_count !== 1 ? 's' : ''}
                      </span>
                    </div>
                    {v.last_sync_error && (
                      <p className="text-[10px] text-red-700 mt-1 truncate" title={v.last_sync_error}>
                        Error: {v.last_sync_error}
                      </p>
                    )}
                  </div>
                  {triggerSync && (
                    <button
                      onClick={() => handleSync(v.villa_id)}
                      disabled={isSyncing}
                      data-testid={`sync-now-${v.villa_id}`}
                      className={`flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[10px] font-semibold transition-colors ${isSyncing ? 'bg-gray-200 text-gray-500' : 'bg-[#F57C20] text-white hover:bg-[#E06A10]'}`}
                    >
                      <FiRefreshCw className={`w-3 h-3 ${isSyncing ? 'animate-spin' : ''}`} />
                      {isSyncing ? 'Syncing...' : 'Sync Now'}
                    </button>
                  )}
                </div>

                {outboundUrl && (
                  <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-gray-200">
                    <span className="text-[9px] uppercase tracking-wider text-gray-500 font-semibold">Outbound URL:</span>
                    <button
                      onClick={() => handleCopy(v.villa_id, outboundUrl)}
                      data-testid={`copy-url-${v.villa_id}`}
                      className={`flex-1 inline-flex items-center justify-center gap-1 px-2 py-1 rounded text-[10px] font-mono truncate ${copied === v.villa_id ? 'bg-green-100 text-green-800' : 'bg-white border border-gray-200 hover:border-[#F57C20] text-gray-700'}`}
                    >
                      <FiCopy className="w-3 h-3 flex-shrink-0" />
                      {copied === v.villa_id ? 'Copied!' : 'Copy iCal URL for Airbnb'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          <p className="text-[9px] text-gray-400 text-center italic mt-2">
            Auto-refreshes every 30 seconds · Airbnb polls RGV feed every 1–3 hours
          </p>
        </div>
      )}
    </div>
  );
};

export default SyncHealthWidget;
